import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Star, Lock, BookOpen, Headphones, Download, Rss, Mail } from "lucide-react";
import { getAllSeries, getSeriesBySlug, getChapter } from "../data/series";
import { useAuth } from "../AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const series = getAllSeries();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [continueReading, setContinueReading] = useState([]);
  const [continueListening, setContinueListening] = useState([]);

  // Fetch continue reading + listening data
  useEffect(() => {
    if (!user) { setContinueReading([]); setContinueListening([]); return; }
    const fetchData = async () => {
      try {
        const [readRes, listenRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/continue-reading`),
          axios.get(`${BACKEND_URL}/api/continue-listening`),
        ]);
        setContinueReading(readRes.data || []);
        setContinueListening(listenRes.data || []);
      } catch { /* ignore */ }
    };
    fetchData();
  }, [user]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await axios.post(`${BACKEND_URL}/api/subscribe`, { email });
      setSubmitStatus({ type: "success", message: "Welcome to Freddy Fiction! Check your inbox." });
      setEmail("");
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center bg-ff-bg overflow-hidden"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-ff-dark-section/50 via-ff-bg to-ff-bg" />
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 text-center pt-20">
          {/* Eyebrow */}
          <p 
            className="label text-ff-accent mb-6 animate-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            FREDDY FICTION
          </p>
          
          {/* Main Headline */}
          <h1 
            className="font-serif text-hero text-ff-text mb-8 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            Stories that stay with you forever.
          </h1>
          
          {/* Subhead */}
          <p 
            className="font-serif italic text-xl md:text-2xl text-ff-text-muted mb-12 max-w-2xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            Serialized fiction for people who ask too many questions.
          </p>
          
          {/* CTAs */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <Link
              to="/subscribe"
              data-testid="hero-subscribe-btn"
              className="btn-primary flex items-center gap-2"
            >
              Subscribe Free
            </Link>
            <Link
              to="/library"
              data-testid="hero-library-btn"
              className="btn-secondary flex items-center gap-2"
            >
              Browse the Library
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Daily delivery pitch */}
          <p
            className="mt-6 text-ff-text-muted text-sm font-light animate-fade-in"
            style={{ animationDelay: "0.45s" }}
          >
            <Mail size={13} className="inline mr-1.5 -mt-0.5" />
            Pick any story and get a chapter a day mailed to you — free.{" "}
            <Link to="/library" className="text-ff-accent hover:underline">Choose a story</Link>
          </p>

          {/* Feature pills */}
          <div
            className="flex flex-wrap items-center justify-center gap-3 mt-8 animate-fade-in"
            style={{ animationDelay: "0.5s" }}
          >
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-ff-text-muted border border-ff-border rounded-full">
              <BookOpen size={13} /> Read online
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-ff-text-muted border border-ff-border rounded-full">
              <Headphones size={13} /> Listen anywhere
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-ff-text-muted border border-ff-border rounded-full">
              <Download size={13} /> Download for offline
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-ff-text-muted border border-ff-border rounded-full">
              <Mail size={13} /> Daily to your inbox
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-ff-text-muted border border-ff-border rounded-full">
              <Rss size={13} /> RSS feeds
            </span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-ff-border rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-ff-text-muted rounded-full" />
          </div>
        </div>
      </section>

      {/* Continue Reading - shown for logged-in users with reading history */}
      {user && continueReading.length > 0 && (
        <section data-testid="continue-reading" className="py-16 bg-ff-dark-section">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-8">
              <BookOpen size={18} className="text-ff-accent" />
              <h2 className="font-serif text-xl text-ff-text">Continue Reading</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {continueReading.filter(cr => !cr.completed).slice(0, 3).map((cr) => {
                const s = getSeriesBySlug(cr.series_slug);
                const ch = getChapter(cr.series_slug, cr.chapter_number);
                if (!s || !ch) return null;
                return (
                  <Link
                    key={`${cr.series_slug}-${cr.chapter_number}`}
                    to={`/${cr.series_slug}/chapter-${cr.chapter_number}`}
                    data-testid={`continue-${cr.series_slug}-${cr.chapter_number}`}
                    className="group flex items-center gap-4 p-4 bg-ff-surface border border-ff-border hover:border-opacity-50 transition-all"
                  >
                    <div className="w-1 h-12 rounded-full shrink-0" style={{ background: s.accentColor }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-ff-text-muted uppercase tracking-wider mb-1">{s.title}</p>
                      <p className="font-serif text-ff-text truncate">Ch {cr.chapter_number}: {ch.title}</p>
                      <div className="mt-2 h-1 bg-ff-border rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${cr.scroll_pct || 0}%`, background: s.accentColor }} />
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-ff-text-muted shrink-0 group-hover:translate-x-1 transition-transform" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Continue Listening - shown for users with listening history */}
      {user && continueListening.length > 0 && (
        <section data-testid="continue-listening" className="py-16 bg-ff-dark-section" style={continueReading.length > 0 ? { paddingTop: 0 } : {}}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-8">
              <Headphones size={18} className="text-ff-accent" />
              <h2 className="font-serif text-xl text-ff-text">Continue Listening</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {continueListening.slice(0, 3).map((cl) => {
                const s = getSeriesBySlug(cl.series_slug);
                const ch = getChapter(cl.series_slug, cl.chapter_number);
                if (!s || !ch) return null;
                const posMin = Math.floor((cl.position || 0) / 60);
                const posSec = Math.floor((cl.position || 0) % 60);
                return (
                  <Link
                    key={`listen-${cl.series_slug}-${cl.chapter_number}`}
                    to={`/${cl.series_slug}/chapter-${cl.chapter_number}`}
                    data-testid={`continue-listen-${cl.series_slug}-${cl.chapter_number}`}
                    className="group flex items-center gap-4 p-4 bg-ff-surface border border-ff-border hover:border-opacity-50 transition-all"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${s.accentColor}20` }}>
                      <Headphones size={16} style={{ color: s.accentColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-ff-text-muted uppercase tracking-wider mb-1">{s.title}</p>
                      <p className="font-serif text-ff-text truncate">Ch {cl.chapter_number}: {ch.title}</p>
                      <p className="text-xs text-ff-text-muted mt-1">
                        Paused at {posMin}:{posSec.toString().padStart(2, '0')}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-ff-text-muted shrink-0 group-hover:translate-x-1 transition-transform" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Series Shelf */}
      <section 
        data-testid="series-shelf"
        className="py-24 md:py-32 bg-ff-bg"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="label text-ff-text-muted mb-4">THE SERIES</p>
            <h2 className="font-serif text-display text-ff-text mb-3">
              Eleven stories. All free. Start reading.
            </h2>
            <p className="text-ff-text-muted text-sm font-light">New stories added weekly.</p>
          </div>

          {/* Series Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {series.map((s, index) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`series-card-${s.slug}`}
                className="series-card group relative bg-ff-surface border border-ff-border hover:border-opacity-50 transition-all duration-500"
                style={{ 
                  borderTopColor: s.accentColor, 
                  borderTopWidth: "3px",
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Patron badge */}
                {s.tier === "patron" && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full" style={{ background: '#c8860a22', color: '#c8860a', border: '1px solid #c8860a44' }}>
                    <Star size={10} fill="currentColor" />
                    Patron
                  </div>
                )}

                {/* Card Content */}
                <div className="p-6 md:p-8">
                  <p className="label mb-4" style={{ color: s.accentColor }}>{s.tag}</p>
                  <h3 className="font-serif text-2xl text-ff-text mb-3">{s.title}</h3>
                  <p className="text-ff-text-muted text-sm font-light leading-relaxed mb-4">{s.logline}</p>
                  <p className="text-ff-text-muted text-xs font-light mb-5">{s.meta}</p>
                  
                  <div className="flex items-center gap-2 text-sm font-sans transition-colors" style={{ color: s.accentColor }}>
                    {s.tier === "patron" ? (
                      <><Lock size={14} /> Patron Exclusive</>
                    ) : (
                      <>Start Reading <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" /> Chapter 1</>
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500" style={{ backgroundColor: s.accentColor }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section 
        data-testid="subscribe-section"
        className="py-24 md:py-32 bg-ff-dark-section"
      >
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <p className="label text-ff-accent mb-4">YOUR DAILY READ</p>
          <h2 className="font-serif text-display text-ff-text mb-6">
            One chapter a day. Any story you want.
          </h2>
          <p className="text-ff-text-muted font-light mb-10 max-w-lg mx-auto">
            Pick a series from the library and we'll mail you one chapter every day at the time you choose. Work through the whole catalog at your own pace — completely free.
          </p>

          {/* Subscribe Form */}
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="subscribe-email-input"
              className="input-dark flex-1 text-center sm:text-left"
              required
            />
            <button
              type="submit"
              data-testid="subscribe-submit-btn"
              disabled={isSubmitting}
              className="btn-primary whitespace-nowrap disabled:opacity-50"
            >
              {isSubmitting ? "Subscribing..." : "Subscribe Free"}
            </button>
          </form>

          {/* Status Message */}
          {submitStatus && (
            <p 
              className={`mt-4 text-sm ${
                submitStatus.type === "success" ? "text-green-400" : "text-red-400"
              }`}
            >
              {submitStatus.message}
            </p>
          )}

          <p className="mt-6 text-ff-text-muted text-xs">
            Then <Link to="/library" className="text-ff-accent hover:underline">choose a story</Link> and hit "Daily to Inbox" to start your daily chapters.
          </p>
        </div>
      </section>

      {/* Featured Quote Section */}
      <section className="py-24 md:py-32 bg-ff-bg">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <blockquote className="font-serif italic text-2xl md:text-4xl text-ff-text leading-relaxed">
            "Fiction built on premises that couldn't leave us alone."
          </blockquote>
          <p className="mt-8 text-ff-text-muted text-sm">
            — The Freddy Fiction Philosophy
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
